import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBrowserWallet } from "@/features/wallet";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../hooks/useAuthModal";
import { SignatureDialog } from "./SignatureDialog";
import { Skeleton } from "../../../components/ui/skeleton";
import { BrandButton } from "../../../components/brand/BrandButton";
import { useToast } from "../../../hooks/useToast";
import { useTranslation } from "@/i18n";
import { Wallet } from "lucide-react";

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  /**
   * Content to render when authenticated
   */
  children: React.ReactNode;

  /**
   * Whether authentication is required to access this route
   * @default true
   */
  requireAuth?: boolean;

  /**
   * Optional custom loading component
   * If not provided, uses default skeleton loader
   */
  loadingComponent?: React.ReactNode;

  /**
   * Optional redirect path for unauthenticated users
   * If not provided, shows login prompt in place
   * @default undefined (shows login prompt)
   */
  redirectTo?: string;

  /**
   * Optional custom message to show when redirecting
   */
  redirectMessage?: string;
}

/**
 * Default loading component with skeleton
 * Mimics typical page layout for smooth transition
 */
function DefaultLoadingState() {
  return (
    <div className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <Skeleton className="h-12 w-64" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * ProtectedRoute Component
 *
 * Higher-order component that protects routes requiring authentication.
 * Checks authentication status and redirects unauthenticated users or shows login prompt.
 *
 * Features:
 * - Checks authentication status using useAuth hook
 * - Shows loading state while verifying authentication
 * - Preserves intended destination for post-login redirect
 * - Handles token expiry gracefully
 * - Provides clear error messages via toast notifications
 * - Supports custom redirect paths or in-place login prompts
 *
 * @example
 * Basic usage with authentication required:
 * ```tsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @example
 * With custom redirect path:
 * ```tsx
 * <ProtectedRoute redirectTo="/login" redirectMessage="Please log in to continue">
 *   <PrivateContent />
 * </ProtectedRoute>
 * ```
 *
 * @example
 * With custom loading component:
 * ```tsx
 * <ProtectedRoute loadingComponent={<CustomSpinner />}>
 *   <ProtectedContent />
 * </ProtectedRoute>
 * ```
 *
 * @example
 * Optional protection (public route):
 * ```tsx
 * <ProtectedRoute requireAuth={false}>
 *   <PublicContent />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  loadingComponent,
  redirectTo,
  redirectMessage = "Please authenticate to access this page",
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, error } = useAuth();
  const { connected } = useBrowserWallet();
  const navigate = useNavigate();
  const location = useLocation();
  const { error: showError } = useToast();
  const {
    isDialogOpen,
    openAuth,
    closeDialog,
    confirmSign,
    step,
    challengeMessage,
    walletAddress,
    isLoading: isAuthLoading,
    error: authError,
    retry,
  } = useAuthModal();

  // Track previous connected state to detect disconnection
  const prevConnectedRef = useRef(connected);

  // True authentication requires both JWT validity AND wallet connection
  const isFullyAuthenticated = isAuthenticated && connected;

  /**
   * Handle authentication check and redirect
   */
  useEffect(() => {
    // Detect wallet disconnection (transition from connected to disconnected)
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = connected;

    // Skip if authentication not required
    if (!requireAuth) {
      return;
    }

    // Don't process while still loading
    if (isLoading) {
      return;
    }

    // If wallet just disconnected (was connected, now not) and has JWT, prompt to reconnect
    // Only show toast on actual disconnection, not on initial render or repeated renders
    if (wasConnected && !connected && isAuthenticated) {
      showError("Wallet Disconnected", {
        id: "wallet-disconnected",
        description: "Please reconnect your wallet to continue",
      });
    }

    // If not fully authenticated, handle redirect.
    // The page itself renders an inline "Wallet connection required" prompt,
    // so we intentionally do NOT fire a toast here — it would duplicate the
    // centered CTA the user is already looking at.
    if (!isFullyAuthenticated && redirectTo) {
      navigate(redirectTo, {
        replace: true,
        state: {
          from: location.pathname + location.search,
          message: redirectMessage,
        },
      });
    }

    // Show error message if present
    if (error && !isFullyAuthenticated) {
      showError("Authentication Error", {
        id: "auth-error",
        description: error,
      });
    }
  }, [
    isAuthenticated,
    connected,
    isFullyAuthenticated,
    isLoading,
    error,
    requireAuth,
    navigate,
    location,
    redirectTo,
    redirectMessage,
    showError,
  ]);

  // Show loading state while checking authentication
  if (isLoading && requireAuth) {
    return <>{loadingComponent || <DefaultLoadingState />}</>;
  }

  // If authentication required but user not fully authenticated
  if (requireAuth && !isFullyAuthenticated) {
    // If no redirect path provided, show in-place login prompt
    if (!redirectTo) {
      const isWalletDisconnected = isAuthenticated && !connected;
      return (
        <>
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className="text-center max-w-md">
              <h2 className="text-2xl font-semibold mb-2">
                {isWalletDisconnected
                  ? t("auth.walletDisconnected", "Wallet Disconnected")
                  : t("auth.walletRequired", "Authentication Required")}
              </h2>
              <p className="text-muted-foreground mb-6">
                {isWalletDisconnected
                  ? t(
                      "auth.reconnectWalletDesc",
                      "Your wallet has been disconnected. Please reconnect to continue.",
                    )
                  : redirectMessage}
              </p>
              <BrandButton
                variant="primary"
                size="lg"
                onClick={openAuth}
                className="gap-2"
              >
                <Wallet className="w-5 h-5" />
                {isWalletDisconnected
                  ? t("auth.reconnectWallet", "Reconnect Wallet")
                  : t("auth.connectAndSignIn", "Connect & Sign In")}
              </BrandButton>
            </div>
          </div>
          <SignatureDialog
            open={isDialogOpen}
            onOpenChange={(open) => !open && closeDialog()}
            step={step}
            challengeMessage={challengeMessage}
            walletAddress={walletAddress}
            error={authError}
            isLoading={isAuthLoading}
            onConfirm={confirmSign}
            onRetry={retry}
          />
        </>
      );
    }

    // If redirect path provided, return null (redirect handled in useEffect)
    return null;
  }

  // Render protected content if fully authenticated or auth not required
  return <>{children}</>;
}

/**
 * Re-export for convenience
 */
export default ProtectedRoute;
